import React, { useState, useEffect } from 'react';
import {
    Plus, FileText, Upload, Download, Receipt,
    Trash2, X, CheckCircle, Calculator, Wallet, ArrowRight, Save,
    Eye, Paperclip, Calendar, User, Tag, Edit3, MessageSquare, Loader2,
    History
} from 'lucide-react';
import api from '../api/axiosInstance';

const ClientBilling = () => {
    const [activeModal, setActiveModal] = useState(null);
    const [invoiceType, setInvoiceType] = useState('flat');
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);

    // Drill-down & Edit State
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceHistory, setInvoiceHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null);

    // Invoice Form State
    const [bill, setBill] = useState({
        client_id: '',
        project_id: '',
        invoice_no: `LI/${new Date().getFullYear()}/${Math.floor(1000 + Math.random() * 9000)}`,
        invoice_date: new Date().toISOString().split('T')[0],
        taxable_amount: 0,
        gst_rate: 18,
        cgst: 0,
        sgst: 0,
        total: 0,
        items: [{ id: Date.now(), desc: '', qty: 1, rate: 0, total: 0 }]
    });

    // Payment Form State
    const [payForm, setPayForm] = useState({
        invoice_id: '',
        amount: '',
        tds: 0,
        date: new Date().toISOString().split('T')[0],
        mode: 'NEFT',
        reference_no: '',
        notes: '',
        proof: null
    });

    const loadData = async () => {
        try {
            const [clientRes, projectRes, invoiceRes] = await Promise.all([
                api.get('get_directory.php?type=client'),
                api.get('get_projects.php'),
                api.get('get_client_invoices.php')
            ]);
            console.log("Clients:", clientRes.data.data);
            console.log("Projects:", projectRes.data.projects);
            console.log("Invoices:", invoiceRes.data.data);
            setClients(clientRes.data.data || []);
            setProjects(projectRes.data.projects || []);
            setInvoices(invoiceRes.data.data || []);
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    // Automatic Calculations
    useEffect(() => {
        let subtotal = 0;
        if (invoiceType === 'itemized') {
            subtotal = bill.items.reduce((sum, i) => sum + (i.qty * i.rate), 0);
        } else {
            subtotal = parseFloat(bill.taxable_amount) || 0;
        }
        const gstHalf = (bill.gst_rate / 2) / 100;
        const cgstAmt = subtotal * gstHalf;
        const sgstAmt = subtotal * gstHalf;

        setBill(prev => ({
            ...prev,
            taxable_amount: subtotal,
            cgst: cgstAmt,
            sgst: sgstAmt,
            total: subtotal + cgstAmt + sgstAmt
        }));
    }, [bill.taxable_amount, bill.gst_rate, bill.items, invoiceType]);

    const viewInvoiceDetails = async (inv) => {
        setSelectedInvoice(inv);
        setLoadingHistory(true);
        setEditingPaymentId(null);
        try {
            const res = await api.get(`get_invoice_history.php?invoice_id=${inv.id}`);
            setInvoiceHistory(res.data.payments || []);
        } catch (err) {
            console.error("History fetch failed");
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSaveInvoice = async (e) => {
        e.preventDefault();
        if (!bill.client_id || !bill.project_id) return alert("Select Client and Project");

        setSaveLoading(true);
        try {
            const res = await api.post('save_client_invoice.php', {
                ...bill,
                invoice_type: invoiceType // Ensure backend knows the type
            });
            if (res.data.status === 'success') {
                alert("Invoice Saved!");
                setActiveModal(null);
                loadData();
                // Reset form
                setBill({
                    ...bill,
                    invoice_no: `LI/${new Date().getFullYear()}/${Math.floor(1000 + Math.random() * 9000)}`,
                    taxable_amount: 0,
                    items: [{ id: Date.now(), desc: '', qty: 1, rate: 0, total: 0 }]
                });
            }
        } catch (err) {
            alert("Save failed: " + err.message);
        } finally {
            setSaveLoading(false);
        }
    };

    const handleRecordPayment = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        Object.keys(payForm).forEach(key => formData.append(key, payForm[key]));
        try {
            const res = await api.post('record_client_payment.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status === 'success') {
                alert("Payment Recorded!");
                setActiveModal(null);
                loadData();
                if (selectedInvoice) viewInvoiceDetails(selectedInvoice);
            }
        } catch (err) {

            alert("Payment failed");
        }
    };

    const handleUpdatePayment = async (pay) => {
        try {
            const res = await api.post('update_client_payment.php', {
                payment_id: pay.id,
                invoice_id: selectedInvoice.id,
                amount: pay.received_amount,
                tds: pay.tds_deducted,
                reference_no: pay.transaction_ref,
                notes: pay.notes,
                date: pay.payment_date
            });

            if (res.data.status === 'success') {
                alert("Payment Updated!");
                setEditingPaymentId(null);
                viewInvoiceDetails(selectedInvoice);
                loadData();
            } else {
                alert("Error: " + res.data.message);
            }
        } catch (err) {
            alert("Update failed.");
        }
    };

    return (
        <div className="space-y-8 relative p-4">
            <div className="flex justify-between items-center">
                <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">Billing Center</h1>
                <button onClick={() => setActiveModal('new_invoice')} className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black shadow-xl flex items-center gap-2 hover:bg-indigo-700 transition-all">
                    <Plus size={20} /> New Invoice
                </button>
            </div>

            {/* MAIN INVOICE LISTING */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <tr>
                            <th className="p-6">Invoice Details</th>
                            <th className="p-6">Site Info</th>
                            <th className="p-6">Bill Total</th>
                            <th className="p-6">Received</th>
                            <th className="p-6 text-rose-500">Balance</th>
                            <th className="p-6 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 font-bold text-sm">
                        {invoices.map((inv) => (
                            <tr key={inv.id} className="hover:bg-indigo-50/50 transition-all cursor-pointer group" onClick={() => viewInvoiceDetails(inv)}>
                                <td className="p-6">
                                    <p className="font-black text-slate-900 group-hover:text-indigo-600 transition-colors">{inv.invoice_no}</p>
                                    <p className="text-[10px] text-slate-400 font-black">{inv.invoice_date}</p>
                                </td>
                                <td className="p-6 uppercase">
                                    <p className="text-xs font-black text-slate-700">{inv.project_name}</p>
                                    <p className="text-[10px] text-slate-400">{inv.client_name}</p>
                                </td>
                                <td className="p-6 font-black text-slate-900">₹{parseFloat(inv.total_amount).toLocaleString()}</td>
                                <td className="p-6 font-black text-emerald-600">₹{parseFloat(inv.paid_amount || 0).toLocaleString()}</td>
                                <td className="p-6 font-black text-rose-500 italic">₹{(inv.total_amount - (inv.paid_amount || 0)).toLocaleString()}</td>
                                <td className="p-6 text-right" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => { setPayForm({ ...payForm, invoice_id: inv.id }); setActiveModal('record_payment'); }} className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase hover:bg-emerald-600 transition-all shadow-md">Add Payment</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MODAL: NEW INVOICE */}
            {activeModal === 'new_invoice' && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleSaveInvoice} className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl font-black italic tracking-tighter uppercase">Generate Invoice</h2>
                            <button type="button" onClick={() => setActiveModal(null)} className="p-2 bg-slate-100 rounded-full"><X /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Select Client</label>
                                <select required className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none ring-2 ring-transparent focus:ring-indigo-500 transition-all" value={bill.client_id} onChange={e => setBill({ ...bill, client_id: e.target.value })}>
                                    <option value="">Choose Client</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Link Project</label>
                                <select required className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={bill.project_id} onChange={e => setBill({ ...bill, project_id: e.target.value })}>
                                    <option value="">Choose Project</option>
                                    {projects.filter(p => p.client_id == bill.client_id).map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Invoice #</label>
                                <input className="w-full p-4 bg-slate-100 rounded-2xl font-bold" value={bill.invoice_no} readOnly />
                            </div>
                        </div>

                        <div className="flex gap-4 p-2 bg-slate-100 rounded-2xl w-max">
                            <button type="button" onClick={() => setInvoiceType('flat')} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${invoiceType === 'flat' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Flat Amount</button>
                            <button type="button" onClick={() => setInvoiceType('itemized')} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${invoiceType === 'itemized' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Itemized List</button>
                        </div>

                        {invoiceType === 'flat' ? (
                            <div className="p-8 bg-slate-50 rounded-3xl space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Taxable Amount (Excl. GST)</label>
                                <input type="number" placeholder="0.00" className="w-full p-6 bg-white rounded-2xl text-3xl font-black text-indigo-600 outline-none" value={bill.taxable_amount} onChange={e => setBill({ ...bill, taxable_amount: e.target.value })} />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {bill.items.map((item, idx) => (
                                    <div key={item.id} className="grid grid-cols-12 gap-4 items-end">
                                        <div className="col-span-6 space-y-1">
                                            <input placeholder="Item Description" className="w-full p-4 bg-slate-50 rounded-2xl font-bold" value={item.desc} onChange={e => {
                                                const newItems = [...bill.items];
                                                newItems[idx].desc = e.target.value;
                                                setBill({ ...bill, items: newItems });
                                            }} />
                                        </div>
                                        <div className="col-span-2">
                                            <input type="number" placeholder="Qty" className="w-full p-4 bg-slate-50 rounded-2xl font-bold" value={item.qty} onChange={e => {
                                                const newItems = [...bill.items];
                                                newItems[idx].qty = e.target.value;
                                                setBill({ ...bill, items: newItems });
                                            }} />
                                        </div>
                                        <div className="col-span-3">
                                            <input type="number" placeholder="Rate" className="w-full p-4 bg-slate-50 rounded-2xl font-bold" value={item.rate} onChange={e => {
                                                const newItems = [...bill.items];
                                                newItems[idx].rate = e.target.value;
                                                setBill({ ...bill, items: newItems });
                                            }} />
                                        </div>
                                        <div className="col-span-1 py-4">
                                            <button type="button" onClick={() => setBill({ ...bill, items: bill.items.filter(i => i.id !== item.id) })} className="text-rose-500"><Trash2 size={20} /></button>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setBill({ ...bill, items: [...bill.items, { id: Date.now(), desc: '', qty: 1, rate: 0, total: 0 }] })} className="text-xs font-black text-indigo-600 uppercase flex items-center gap-2"><Plus size={14} /> Add Row</button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-10 border-t pt-8">
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-black text-slate-400 uppercase">GST Rate</span>
                                    <select className="p-2 bg-slate-100 rounded-lg font-bold" value={bill.gst_rate} onChange={e => setBill({ ...bill, gst_rate: e.target.value })}>
                                        <option value="18">18% (Standard)</option>
                                        <option value="12">12%</option>
                                        <option value="5">5%</option>
                                        <option value="0">Exempt</option>
                                    </select>
                                </div>
                                <div className="flex justify-between text-xs font-bold text-slate-500 uppercase"><span>CGST ({bill.gst_rate / 2}%)</span><span>₹{bill.cgst.toLocaleString()}</span></div>
                                <div className="flex justify-between text-xs font-bold text-slate-500 uppercase"><span>SGST ({bill.gst_rate / 2}%)</span><span>₹{bill.sgst.toLocaleString()}</span></div>
                            </div>
                            <div className="text-right space-y-2">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Grand Total</p>
                                <h3 className="text-5xl font-black text-slate-900 italic tracking-tighter">₹{bill.total.toLocaleString()}</h3>
                            </div>
                        </div>

                        <button disabled={saveLoading} className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl flex items-center justify-center gap-3 hover:bg-indigo-600 transition-all">
                            {saveLoading ? <Loader2 className="animate-spin" /> : <Save size={24} />}
                            Save & Generate Invoice
                        </button>
                    </form>
                </div>
            )}




            {/* --- DRILL-DOWN: INVOICE DETAIL SIDE PANEL --- */}
            {selectedInvoice && (
                <div className="fixed inset-0 z-[120] flex items-center justify-end bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white h-full w-full max-w-xl shadow-2xl p-10 overflow-y-auto animate-in slide-in-from-right duration-300">
                        {/* PANEL HEADER */}
                        <div className="flex justify-between items-center mb-10 border-b border-slate-50 pb-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tighter italic uppercase">{selectedInvoice.invoice_no}</h2>
                                <div className="flex gap-2 mt-1">
                                    <span className="text-[9px] font-black bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full uppercase">{selectedInvoice.project_name}</span>
                                    <span className="text-[9px] font-black bg-slate-100 text-slate-500 px-3 py-1 rounded-full uppercase">{selectedInvoice.client_name}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedInvoice(null)}
                                className="p-3 bg-slate-100 rounded-2xl hover:bg-rose-50 hover:text-rose-500 transition-all"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* BALANCE CARD */}
                        <div className="p-8 bg-slate-900 rounded-[40px] text-white shadow-xl mb-10 relative overflow-hidden">
                            <div className="relative z-10">
                                <div className="space-y-3">
                                    <div className="flex justify-between opacity-60 text-[10px] font-black uppercase tracking-widest">
                                        <span>Grand Total</span>
                                        <span>₹{parseFloat(selectedInvoice.total_amount).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-3xl font-black pt-4 border-t border-white/10 italic">
                                        <span>Balance Due</span>
                                        <span className="text-rose-400">₹{(selectedInvoice.total_amount - (selectedInvoice.paid_amount || 0)).toLocaleString()}</span>
                                    </div>
                                </div>
                                <div className="mt-6 flex gap-4">
                                    <div className="flex-1 bg-white/10 p-3 rounded-2xl border border-white/5">
                                        <p className="text-[8px] font-black uppercase text-indigo-300">Total Received</p>
                                        <p className="font-bold text-emerald-400 text-sm">₹{parseFloat(selectedInvoice.paid_amount || 0).toLocaleString()}</p>
                                    </div>
                                    <div className="flex-1 bg-white/10 p-3 rounded-2xl border border-white/5">
                                        <p className="text-[8px] font-black uppercase text-indigo-300">Invoice Date</p>
                                        <p className="font-bold text-white text-sm">{selectedInvoice.invoice_date}</p>
                                    </div>
                                </div>
                            </div>
                            <Calculator size={120} className="absolute -right-8 -bottom-8 text-white/5 rotate-12" />
                        </div>

                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                            <History size={14} className="text-indigo-500" /> Transaction Timeline
                        </h3>

                        {/* PAYMENT LISTING */}
                        {loadingHistory ? (
                            <div className="py-20 text-center">
                                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="font-black italic text-slate-300 text-sm uppercase">Loading Ledger...</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {invoiceHistory.length === 0 ? (
                                    <div className="bg-slate-50 rounded-3xl p-10 text-center border border-dashed border-slate-200">
                                        <p className="text-slate-400 font-bold italic">No payments recorded yet for this invoice.</p>
                                    </div>
                                ) : (
                                    invoiceHistory.map((pay, i) => (
                                        <div key={pay.id || i} className={`p-6 rounded-[32px] border transition-all ${editingPaymentId === pay.id ? 'bg-indigo-50 border-indigo-200 ring-2 ring-indigo-500/20' : 'bg-white border-slate-100 hover:shadow-md'}`}>
                                            {editingPaymentId === pay.id ? (
                                                /* --- EDIT PAYMENT FORM --- */
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-black text-slate-400 uppercase ml-2">Received Amount</label>
                                                            <input
                                                                type="number"
                                                                className="w-full p-4 bg-white rounded-2xl font-black text-indigo-600 outline-none border border-indigo-100"
                                                                value={pay.received_amount}
                                                                onChange={(e) => {
                                                                    const updated = [...invoiceHistory];
                                                                    updated[i] = { ...updated[i], received_amount: e.target.value };
                                                                    setInvoiceHistory(updated);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[9px] font-black text-slate-400 uppercase ml-2">Ref / UTR No</label>
                                                            <input
                                                                type="text"
                                                                className="w-full p-4 bg-white rounded-2xl font-bold text-sm outline-none border border-indigo-100"
                                                                value={pay.transaction_ref}
                                                                onChange={(e) => {
                                                                    const updated = [...invoiceHistory];
                                                                    updated[i] = { ...updated[i], transaction_ref: e.target.value };
                                                                    setInvoiceHistory(updated);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <textarea
                                                        className="w-full p-4 bg-white rounded-2xl text-xs font-medium border border-indigo-100 h-24 outline-none"
                                                        value={pay.notes || ''}
                                                        placeholder="Update notes..."
                                                        onChange={(e) => {
                                                            const updated = [...invoiceHistory];
                                                            updated[i] = { ...updated[i], notes: e.target.value };
                                                            setInvoiceHistory(updated);
                                                        }}
                                                    />
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleUpdatePayment(pay)} className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase shadow-lg shadow-indigo-100">Save Changes</button>
                                                        <button onClick={() => setEditingPaymentId(null)} className="px-6 py-4 bg-slate-200 text-slate-600 rounded-2xl font-black text-xs uppercase">Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* --- VIEW PAYMENT ROW --- */
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                                                                <Wallet size={18} />
                                                            </div>
                                                            <div>
                                                                <p className="font-black text-slate-900 text-xl tracking-tight">₹{parseFloat(pay.received_amount).toLocaleString()}</p>
                                                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{pay.payment_date} • {pay.payment_mode}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2 mt-4 text-[10px] font-bold text-slate-500 bg-slate-50 w-max px-3 py-1 rounded-lg">
                                                            <Tag size={12} /> Ref: {pay.transaction_ref || 'N/A'}
                                                        </div>

                                                        {pay.notes && (
                                                            <div className="mt-4 p-4 bg-white rounded-2xl border border-slate-50 flex gap-3 items-start">
                                                                <MessageSquare size={14} className="text-indigo-400 mt-1 shrink-0" />
                                                                <p className="text-xs text-slate-500 italic leading-relaxed">{pay.notes}</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-col gap-2">
                                                        <button
                                                            onClick={() => setEditingPaymentId(pay.id)}
                                                            className="p-3 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-indigo-600 hover:border-indigo-100 transition-all shadow-sm"
                                                            title="Edit Entry"
                                                        >
                                                            <Edit3 size={16} />
                                                        </button>
                                                        {pay.receipt_copy_path && (
                                                            <a
                                                                href={`${process.env.REACT_APP_API_URL}/${pay.receipt_copy_path}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="p-3 bg-white border border-slate-100 rounded-xl text-indigo-500 hover:bg-indigo-50 transition-all shadow-sm"
                                                                title="View Proof"
                                                            >
                                                                <Eye size={16} />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* FOOTER ACTION */}
                        <button
                            onClick={() => { setPayForm({ ...payForm, invoice_id: selectedInvoice.id }); setActiveModal('record_payment'); }}
                            className="w-full mt-10 py-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-emerald-600 transition-all flex items-center justify-center gap-3 shadow-xl"
                        >
                            <Plus size={18} /> Record New Receipt
                        </button>
                    </div>
                </div>
            )}

            {/* MODAL: RECORD PAYMENT */}
            {activeModal === 'record_payment' && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleRecordPayment} className="bg-white w-full max-w-xl rounded-[48px] shadow-2xl p-12 space-y-6">
                        <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-black italic tracking-tighter">Record Receipt</h2><button type="button" onClick={() => setActiveModal(null)}><X /></button></div>
                        <div className="grid grid-cols-2 gap-4">
                            <input required type="number" placeholder="Amt Received" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setPayForm({ ...payForm, amount: e.target.value })} />
                            <input type="number" placeholder="TDS" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setPayForm({ ...payForm, tds: e.target.value })} />
                        </div>
                        <input type="text" placeholder="Ref No (UTR / Check)" className="w-full bg-slate-50 p-4 rounded-2xl font-bold outline-none" onChange={e => setPayForm({ ...payForm, reference_no: e.target.value })} />
                        <textarea placeholder="Payment Notes (e.g. Received via GPay for Site Handover)" className="w-full bg-slate-50 p-4 rounded-2xl font-bold text-sm h-24 outline-none" onChange={e => setPayForm({ ...payForm, notes: e.target.value })} />
                        <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center hover:border-indigo-500 transition-all">
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => setPayForm({ ...payForm, proof: e.target.files[0] })} />
                            <Upload className="mx-auto text-slate-300 mb-2" /><span className="text-xs font-bold text-slate-500">{payForm.proof ? payForm.proof.name : 'Upload Screenshot / PDF'}</span>
                        </div>
                        <button className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black shadow-xl hover:scale-[1.02] transition-all">Save & Update Ledger</button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ClientBilling;